import { usePaymentMethods } from "@/lib/paymentMethods";
const formatEur = (v) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(v || 0);

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR");
  } catch {
    return String(iso).slice(0, 10);
  }
}

export function DocumentTotals({ totalHT, totalTVA, totalTTC, depositAmount, netToPay, acompteDeductions, payments }) {
  const pm = usePaymentMethods();
  const deductions = Array.isArray(acompteDeductions) ? acompteDeductions : [];
  const hasDeductions = deductions.length > 0;
  const sumDeductions = deductions.reduce((s, a) => s + Number(a?.amount_ttc || 0), 0);
  const resteAPayer = Number(totalTTC || 0) - sumDeductions;

  const pays = Array.isArray(payments) ? payments : [];
  const sumPayments = pays.reduce((s, p) => s + Number(p?.amount_ttc || 0), 0);
  const hasPayments = pays.length > 0 && sumPayments > 0;

  // Base à partir de laquelle on déduit les règlements
  const baseDue = hasDeductions ? resteAPayer : (depositAmount > 0 ? netToPay : Number(totalTTC || 0));
  const soldeAPayer = baseDue - sumPayments;

  // Cas 1 : facture avec déductions d'acomptes ou règlements enregistrés → affichage vertical détaillé
  if (hasDeductions || hasPayments) {
    return (
      <div className="p-3 bg-slate-50 rounded-md" data-testid="doc-totals">
        <div className="flex justify-end gap-6 flex-wrap">
          <div className="text-right">
            <p className="text-xs text-slate-500 uppercase">Total HT</p>
            <p className="text-base font-semibold">{formatEur(totalHT)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500 uppercase">TVA</p>
            <p className="text-base font-semibold">{formatEur(totalTVA)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500 uppercase">Total TTC</p>
            <p className="text-lg font-bold">{formatEur(totalTTC)}</p>
          </div>
        </div>

        {hasDeductions && (
          <div className="mt-3 pt-3 border-t border-slate-200 flex flex-col items-end gap-1" data-testid="doc-totals-deductions">
            {deductions.map((a, i) => (
              <div key={a.invoice_id || `ded-${i}`} className="flex items-baseline gap-4 text-sm">
                <span className="text-amber-700">
                  Acompte {a.invoice_number || ""}{a.pct ? ` (${Math.round(Number(a.pct))}\u00A0%)` : ""}
                </span>
                <span className="font-mono font-semibold text-amber-700 min-w-[110px] text-right">
                  -{formatEur(Number(a.amount_ttc || 0))}
                </span>
              </div>
            ))}
            <div className="flex items-baseline gap-4 mt-1 pt-1 border-t border-slate-300 w-full max-w-xs justify-end">
              <span className="text-sm text-slate-600 uppercase font-semibold">Reste à payer</span>
              <span className="font-mono text-base font-bold text-slate-900 min-w-[110px] text-right" data-testid="doc-reste-a-payer">
                {formatEur(resteAPayer)}
              </span>
            </div>
          </div>
        )}

        {hasPayments && (
          <div className="mt-3 pt-3 border-t border-slate-200 flex flex-col items-end gap-1" data-testid="doc-totals-payments">
            {pays.map((p, i) => (
              <div key={p.id || `pay-${i}`} className="flex items-baseline gap-4 text-sm">
                <span className="text-emerald-700">
                  Règlement {fmtDate(p.date)}
                  {p.mode ? ` (${pm.label(p.mode)})` : ""}
                </span>
                <span className="font-mono font-semibold text-emerald-700 min-w-[110px] text-right">
                  -{formatEur(Number(p.amount_ttc || 0))}
                </span>
              </div>
            ))}
            <div className="flex items-baseline gap-4 mt-1 pt-1 border-t-2 border-indigo-500 w-full max-w-xs justify-end">
              <span className="text-sm text-indigo-700 uppercase font-semibold">Solde à payer</span>
              <span className="font-mono text-lg font-bold text-indigo-700 min-w-[110px] text-right" data-testid="doc-solde-a-payer">
                {formatEur(soldeAPayer)}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Cas 2 : legacy — un unique acompte simple saisi dans le formulaire
  return (
    <div className="flex justify-end gap-6 p-3 bg-slate-50 rounded-md flex-wrap" data-testid="doc-totals">
      <div className="text-right">
        <p className="text-xs text-slate-500 uppercase">Total HT</p>
        <p className="text-base font-semibold">{formatEur(totalHT)}</p>
      </div>
      <div className="text-right">
        <p className="text-xs text-slate-500 uppercase">TVA</p>
        <p className="text-base font-semibold">{formatEur(totalTVA)}</p>
      </div>
      <div className="text-right">
        <p className="text-xs text-slate-500 uppercase">Total TTC</p>
        <p className="text-lg font-bold">{formatEur(totalTTC)}</p>
      </div>
      {depositAmount > 0 && (
        <>
          <div className="text-right border-l pl-4">
            <p className="text-xs text-amber-600 uppercase">Acompte</p>
            <p className="text-base font-semibold text-amber-700">-{formatEur(depositAmount)}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500 uppercase">Net à payer</p>
            <p className="text-lg font-bold text-indigo-700">{formatEur(netToPay)}</p>
          </div>
        </>
      )}
    </div>
  );
}
